import { AbstractControl, ValidationErrors } from '@angular/forms';

const ERROR_CODES = {
        INVALID_LENGTH: 'INVALID_LENGTH',
        INVALID_FORMAT: 'INVALID_FORMAT',
        INVALID_DATE: 'INVALID_DATE',
        CHECKSUM_FAILED: 'CHECKSUM_FAILED'
    };

function validateSAIdNumber(idNumber: string): { valid: boolean; errorCode?: string; message?: string } {
    // Check if input is empty
    if (!idNumber || idNumber.trim() === '') {
      return { valid: false, errorCode: ERROR_CODES.INVALID_LENGTH, message: 'ID number is required' };
    }

    // Check if input is a string with exactly 13 digits
    if (!/^\d{13}$/.test(idNumber)) {
      const message = idNumber.length < 13
        ? 'ID number length (must be 13 digits)'
        : idNumber.length > 13
          ? 'ID number length (must be 13 digits)'
          : 'ID number invalid';
      return { valid: false, errorCode: ERROR_CODES.INVALID_FORMAT, message };
    }

    // Extract date of birth and check validity
    const year = parseInt(idNumber.substring(0, 2), 10);
    const month = parseInt(idNumber.substring(2, 4), 10);
    const day = parseInt(idNumber.substring(4, 6), 10);

    // Adjust year to full 4 digits
    const fullYear = year + (year < 50 ? 2000 : 1900);

    // Check if date is valid
    const date = new Date(fullYear, month - 1, day);
    if (
      date.getFullYear() !== fullYear ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return {
        valid: false,
        errorCode: ERROR_CODES.INVALID_DATE,
        message: 'ID number (invalid date of birth)'
      };
    }

    // Apply Luhn algorithm
    let sum = 0;
    let alternate = false;

    // Process digits from right to left
    for (let i = idNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(idNumber.charAt(i), 10);

      if (alternate) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      alternate = !alternate;
    }

    // Check if sum is divisible by 10
    const isValid = sum % 10 === 0;

    if (!isValid) {
      return {
        valid: false,
        errorCode: ERROR_CODES.CHECKSUM_FAILED,
        message: 'ID number failed validation'// (algorithm)
      };
    }

    return { valid: true };
}

function extractIdparts(idNumber: string): {
    valid: boolean;
    dateOfBirth?: string;
    age?: number;
    gender?: 'Male' | 'Female';
    citizenStatus?: 'South African Citizen' | 'Permanent Resident';
    idComponents?: {
      birthDate: string;
      genderDigits: string;
      citizenDigit: string;
      raceDigit: string;
      checksumDigit: string;
    };
    errorCode?: string;
    message?: string;
  } {
    const validResult = validateSAIdNumber(idNumber);

    if (!validResult.valid) {
      return validResult;
    }

    const year = parseInt(idNumber.substring(0, 2), 10);
    const month = parseInt(idNumber.substring(2, 4), 10);
    const day = parseInt(idNumber.substring(4, 6), 10);
    const birthSequence = parseInt(idNumber.substring(6, 10), 10);
    const citizen = parseInt(idNumber.charAt(10), 10);

    // Adjust year to full 4 digits
    const fullYear = year + (year < 50 ? 2000 : 1900);

    // Get age calculation
    const birthDate = new Date(fullYear, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    // Format date of birth
    const dob = new Date(fullYear, month - 1, day);
    const formattedDob = dob.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    return {
      valid: true,
      dateOfBirth: formattedDob,
      age: age,
      gender: birthSequence >= 5000 ? 'Male' : 'Female',
      citizenStatus: citizen === 0 ? 'South African Citizen' : 'Permanent Resident',
      idComponents: {
        birthDate: idNumber.substring(0, 6),
        genderDigits: idNumber.substring(6, 10),
        citizenDigit: idNumber.substring(10, 11),
        raceDigit: idNumber.substring(11, 12),
        checksumDigit: idNumber.substring(12, 13)
      }
    };
  }

export function saidValidationValidator(
  control: AbstractControl
): ValidationErrors | null {
  if(control && control?.value){
    const validationResult = validateSAIdNumber(control?.value);
      return (validationResult.valid) ? null : { validationResult };
  }
  return null;
}
